import React from "react";
import StaticPage from "./StaticPage";
import { COMPANY } from "@/lib/siteConfig";

const UPDATED = "3 August 2026";

export default function Privacy() {
  return (
    <StaticPage
      title="Privacy Policy"
      subtitle={`Last updated: ${UPDATED}`}
    >
      <p>
        {COMPANY.name} ("SchoolLane", "we", "us" or "our") is committed to
        protecting the privacy of every school, parent, vendor and visitor
        that interacts with our platform. This Privacy Policy explains what
        information we collect, how we use it, how we protect it and the
        rights available to you under applicable Indian data-protection law.
      </p>

      <h2>1. Who this policy applies to</h2>
      <p>
        This policy applies to all users of the SchoolLane website, mobile
        views and dashboard applications — including school administrators,
        parents, teachers, accountants, suppliers and casual visitors.
      </p>

      <h2>2. Information we collect</h2>

      <h3>2.1 Parent information</h3>
      <ul>
        <li>Name, mobile number, email address</li>
        <li>Delivery address for orders placed through the parent store</li>
        <li>Relationship to the student (parent / guardian)</li>
        <li>Order history and payment status (transaction reference only — card details are handled by our payment processors)</li>
      </ul>

      <h3>2.2 Student-related information</h3>
      <ul>
        <li>Student name, class / section (as authorised by the school)</li>
        <li>Applicable book set or uniform size assigned by the school</li>
      </ul>
      <p>
        SchoolLane does not collect student information directly from parents
        beyond what the school authorises. We do not use student information
        for marketing or advertising and we never sell it to third parties.
      </p>

      <h3>2.3 School information</h3>
      <ul>
        <li>School name, address, city, state, pin code, board affiliation</li>
        <li>Contact details of authorised administrators, accountants and principals</li>
        <li>GST identification number (GSTIN), if applicable</li>
        <li>Approval hierarchy, budget structure and internal user list</li>
        <li>Procurement records — requisitions, purchase orders, invoices and payments</li>
      </ul>

      <h3>2.4 Vendor information</h3>
      <ul>
        <li>Business name, GSTIN, PAN, address, contact persons</li>
        <li>Product catalogue, price lists, delivery terms and payment terms</li>
        <li>Historical performance — quotations submitted, orders fulfilled, invoices raised</li>
      </ul>

      <h3>2.5 Automatically collected information</h3>
      <ul>
        <li>Device type, browser, operating system</li>
        <li>IP address and approximate location (used for rate-limiting and security)</li>
        <li>Pages visited, actions taken, and time spent on the platform</li>
      </ul>

      <h2>3. Cookies and similar technologies</h2>
      <p>
        We use essential cookies to keep you signed in and to protect against
        fraud, and analytics cookies to understand how the platform is used.
        See our separate <a href="/cookies">Cookie Policy</a> for full details
        and the controls available to you.
      </p>

      <h2>4. Analytics</h2>
      <p>
        We use privacy-respecting analytics tools to measure aggregate usage
        of the platform. Analytics identifiers are stored separately from
        transaction data and are never linked to a specific student's records.
      </p>

      <h2>5. How we use your information</h2>
      <ul>
        <li>To provide and operate the platform (order management, approvals, invoicing)</li>
        <li>To communicate with you about your account, orders or support requests</li>
        <li>To send transactional emails such as order confirmations and password-reset links</li>
        <li>To maintain a secure, tamper-evident audit trail for procurement transactions</li>
        <li>To comply with legal, tax and accounting obligations</li>
      </ul>

      <h2>6. Sharing of information</h2>
      <p>
        SchoolLane does not sell your data. We share information only in the
        following circumstances:
      </p>
      <ul>
        <li>With the specific school, vendor or parent involved in a procurement transaction (for example, sharing the school's delivery address with the vendor that will fulfil the order).</li>
        <li>With sub-processors that help us run the platform (email delivery, hosting, payment processing) — all under written data-protection commitments.</li>
        <li>Where required by law, court order or a competent regulatory authority.</li>
      </ul>

      <h2>7. Data retention</h2>
      <p>
        We retain procurement records for the duration required by Indian tax
        and audit legislation (currently a minimum of eight years for
        GST-related records). Account information is retained while your
        account is active and for a reasonable period thereafter so that we
        can respond to disputes and legal requests.
      </p>

      <h2>8. Security</h2>
      <p>
        We apply industry-standard controls to protect your information —
        including encrypted connections (TLS), password hashing, role-based
        access, tenant isolation across schools, brute-force protection and
        a tamper-evident audit log. No system can be guaranteed to be 100%
        secure, but we work continuously to strengthen our defences.
      </p>

      <h2>9. Your rights</h2>
      <p>
        Subject to applicable law, you may:
      </p>
      <ul>
        <li>Ask us for a copy of the personal information we hold about you</li>
        <li>Ask us to correct information that is inaccurate or out of date</li>
        <li>Ask us to delete information that is no longer required by law</li>
        <li>Withdraw any consent you previously provided for optional processing</li>
        <li>Raise a grievance with our Grievance Officer (see below)</li>
      </ul>

      <h2>10. Children</h2>
      <p>
        SchoolLane is designed for use by schools and parents. Students under
        18 do not create SchoolLane accounts directly. Any information about
        students on the platform is authorised and controlled by the school.
      </p>

      <h2>11. Changes to this policy</h2>
      <p>
        We may update this policy from time to time. Where the changes are
        material, we will notify registered users by email or through the
        dashboard.
      </p>

      <h2>12. Contact & Grievance Officer</h2>
      <p>
        For any questions, requests or complaints regarding this Privacy
        Policy or the way we handle your information, please contact us:
      </p>
      <p className="not-prose">
        <strong>{COMPANY.name}</strong><br />
        {COMPANY.address.line1}<br />
        {COMPANY.address.line2}<br />
        {COMPANY.address.line3}<br />
        Phone: <a href={COMPANY.phoneHref}>{COMPANY.phone}</a><br />
        Email: <a href={COMPANY.emailHref}>{COMPANY.email}</a>
      </p>
    </StaticPage>
  );
}
